import type { FormDialogRef } from '@/components'
import {
  Button,
  Field,
  FieldGroup,
  FieldLabel,
  FormDialog,
  NumberInput,
  Textarea,
} from '@/components'
import { MANAGE_ABSENCE_TYPE_TRANSACTIONS_MUTATION } from '@/modules/timesheets/mutations'
import { useFormWatch } from '@/utils'
import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import z from 'zod'
import type { ManagementAbsenceType } from './absence-management'

const ManageAbsenceTypeSchema = z.object({
  add: z.number(),
  subtract: z.number(),
  reason: z.string(),
})

type ManageAbsenceTypeForm = z.infer<typeof ManageAbsenceTypeSchema>

interface Props {
  absenceType: ManagementAbsenceType
  date: Date
}

export function ManageAbsenceTypeDialog({ absenceType, date }: Props) {
  const translations = useTranslate({
    manage: 'settings.absence.manage-absences.manage-button',

    addHours: 'settings.absence.manage-absences.add-hours',
    addDays: 'settings.absence.manage-absences.add-days',
    addAbsences: 'settings.absence.manage-absences.add-absences',

    subtractHours: 'settings.absence.manage-absences.subtract-hours',
    subtractDays: 'settings.absence.manage-absences.subtract-days',
    subtractAbsences: 'settings.absence.manage-absences.subtract-absences',

    reason: 'settings.absence.manage-absences.reason',

    explainAddHours: [
      'settings.absence.manage-absences.explain-add-hours',
      { count: <strong />, user: <strong />, type: <strong /> },
    ],
    explainAddDays: [
      'settings.absence.manage-absences.explain-add-days',
      { count: <strong />, user: <strong />, type: <strong /> },
    ],
    explainAddAbsences: [
      'settings.absence.manage-absences.explain-add-absences',
      { count: <strong />, user: <strong />, type: <strong /> },
    ],

    explainSubtractHours: [
      'settings.absence.manage-absences.explain-subtract-hours',
      { count: <strong />, user: <strong />, type: <strong /> },
    ],
    explainSubtractDays: [
      'settings.absence.manage-absences.explain-subtract-days',
      { count: <strong />, user: <strong />, type: <strong /> },
    ],
    explainSubtractAbsences: [
      'settings.absence.manage-absences.explain-subtract-absences',
      { count: <strong />, user: <strong />, type: <strong /> },
    ],
  })

  const dialogRef = useRef<FormDialogRef>(null)

  const [addTransaction, { loading: addTransactionLoading }] = useMutation(
    MANAGE_ABSENCE_TYPE_TRANSACTIONS_MUTATION,
    {
      refetchQueries: ['AbsenceTypeManagement'],
      awaitRefetchQueries: true,
    }
  )

  const form = useForm({
    resolver: zodResolver(ManageAbsenceTypeSchema),
    defaultValues: {
      add: 0,
      subtract: 0,
      reason: '',
    },
  })

  const add = useFormWatch(form, 'add')
  const subtract = useFormWatch(form, 'subtract')

  const texts = (() => {
    const amount = add - subtract

    const count = <strong>{Math.abs(amount)}</strong>
    const user = <strong>{absenceType.periodReport.user.fullName}</strong>
    const type = <strong>{absenceType.name}</strong>

    const args = { count, user, type }

    if (absenceType.preset === 'FLEXTIME')
      return {
        add: translations.addHours,
        subtract: translations.subtractHours,
        explanation:
          amount >= 0
            ? translations.explainAddHours(args)
            : translations.explainSubtractHours(args),
      }

    if (absenceType.periodReport.absencesMax !== null)
      return {
        add: translations.addAbsences,
        subtract: translations.subtractAbsences,
        explanation:
          amount >= 0
            ? translations.explainAddAbsences(args)
            : translations.explainSubtractAbsences(args),
      }

    if (absenceType.durationMode === 'HOURLY')
      return {
        add: translations.addHours,
        subtract: translations.subtractHours,
        explanation:
          amount >= 0
            ? translations.explainAddHours(args)
            : translations.explainSubtractHours(args),
      }

    return {
      add: translations.addDays,
      subtract: translations.subtractDays,
      explanation:
        amount >= 0
          ? translations.explainAddDays(args)
          : translations.explainSubtractDays(args),
    }
  })()

  function resetForm() {
    setTimeout(() => {
      form.reset()
    }, 300)
  }

  async function handleSubmit(data: ManageAbsenceTypeForm) {
    await addTransaction({
      variables: {
        absenceTypeId: absenceType.id,
        userId: absenceType.periodReport.user.id,
        date: date.toISOString(),
        deduct: -(data.add - data.subtract),
        reason: data.reason,
      },
    })

    dialogRef.current?.close()
  }

  return (
    <FormDialog
      ref={dialogRef}
      className="w-[400px]"
      form={form}
      header={absenceType.name}
      trigger={<Button size="sm">{translations.manage}</Button>}
      confirmDisabled={add - subtract === 0 || addTransactionLoading}
      onClose={resetForm}
      onSubmit={handleSubmit}
    >
      <FieldGroup>
        <Controller
          control={form.control}
          name="add"
          render={({ field }) => (
            <Field>
              <div className="flex items-center justify-between gap-2">
                <FieldLabel htmlFor="manage-add">{texts.add}</FieldLabel>

                <NumberInput
                  {...field}
                  id="manage-add"
                  className="w-min"
                  size="sm"
                  min={0}
                />
              </div>
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="subtract"
          render={({ field }) => (
            <Field>
              <div className="flex items-center justify-between gap-2">
                <FieldLabel htmlFor="manage-subtract">
                  {texts.subtract}
                </FieldLabel>

                <NumberInput
                  {...field}
                  id="manage-subtract"
                  className="w-min"
                  size="sm"
                  min={0}
                />
              </div>
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="reason"
          render={({ field }) => (
            <Field>
              <FieldLabel htmlFor="manage-reason">
                {translations.reason}
              </FieldLabel>

              <Textarea {...field} rows={4} />
            </Field>
          )}
        />
      </FieldGroup>

      <p className="mt-4 text-sm text-pretty">{texts.explanation}</p>
    </FormDialog>
  )
}
